import { useEffect, useMemo, useState } from 'react'

import { Button, Input, Label, toast } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ArticleAutocomplete, VehicleAutocomplete } from '@/components/common'
import { DateInput } from '@/components/ui/DateInput'
import { useVehiclesStore } from '@/store/vehicles.store'
import { useWarehouseStore } from '@/store/warehouse.store'
import { todayISO } from '@/utils/dates'

const articleSchema = z.object({
  name: z.string().min(1, 'Укажите наименование детали').max(512, 'Слишком длинное наименование'),
  date: z.string().min(1, 'Укажите дату установки'),
  count: z.number({ error: 'Укажите количество' }).int('Количество должно быть целым числом').min(1, 'Количество должно быть больше 0'),
})

type FormValues = z.infer<typeof articleSchema>;

interface WarehouseInstallFormProps {
  articleId?: string;
  initialArticleName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function WarehouseInstallForm({ articleId, initialArticleName, onSuccess, onCancel }: WarehouseInstallFormProps) {
  const { t } = useTranslation()
  const isEdit = Boolean(articleId)

  const { history, vehicleReport, addInstallation, updateInstallation, isLoading } = useWarehouseStore()
  const { vehicles, fetchVehicles } = useVehiclesStore()

  // Load vehicles lists
  useEffect(() => {
    if (vehicles.length === 0) {
      fetchVehicles()
    }
  }, [vehicles.length, fetchVehicles])

  const existing = useMemo(() => {
    if (!articleId) return undefined
    return history.find(h => h.id === articleId) || vehicleReport.find(r => r.id === articleId)
  }, [articleId, history, vehicleReport])

  // Track selected vehicle plate and ID
  const [vehicleId, setVehicleId] = useState<string>('none')
  const [vehiclePlate, setVehiclePlate] = useState<string | null>(null)

  // Resolve initial vehicle selection on edit
  useEffect(() => {
    if (existing && vehicles.length > 0) {
      const vId = 'vehicleId' in existing ? existing.vehicleId : null
      const vPlate = 'vehiclePlate' in existing ? existing.vehiclePlate : null
      const found = vehicles.find(v => v.id === vId || (vPlate && v.plate.toUpperCase() === vPlate.toUpperCase()))
      if (found) {
        setVehicleId(found.id)
        setVehiclePlate(found.plate)
      }
    }
  }, [existing, vehicles])

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(articleSchema),
    defaultValues: existing
      ? {
          name: ('parsedName' in existing ? (existing as any).parsedName : existing.name) || '',
          date: existing.date,
          count: existing.count,
        }
      : { name: initialArticleName || '', date: todayISO(), count: 1 },
  })

  useEffect(() => {
    if (existing) {
      reset({
        name: ('parsedName' in existing ? (existing as any).parsedName : existing.name) || '',
        date: existing.date,
        count: existing.count,
      })
    } else {
      reset({ name: initialArticleName || '', date: todayISO(), count: 1 })
      setVehicleId('none')
      setVehiclePlate(null)
    }
  }, [existing, reset, initialArticleName])

  const onSubmit = async (data: FormValues) => {
    if (!vehiclePlate || vehicleId === 'none') {
      toast.danger('Пожалуйста, выберите транспортное средство')
      return
    }

    const payload = {
      name: data.name.trim(),
      vehicleId: vehicleId,
      date: data.date,
      count: data.count,
    }

    try {
      if (isEdit && articleId) {
        await updateInstallation(articleId, payload)
      } else {
        await addInstallation(payload)
      }
      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      console.error(err)
    }
  }

  // Find vehicle info for autocomplete initial mapping
  const initialVehicle = useMemo(() => {
    if (vehicleId !== 'none') {
      const v = vehicles.find(v => v.id === vehicleId)
      if (v) {
        return {
          ID: v.id,
          Number: v.plate,
          Model: v.brand,
          Make: v.year,
        }
      }
    }
    return null
  }, [vehicleId, vehicles])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
      {/* Part Name */}
      <div className="flex flex-col gap-1">
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            initialArticleName || isEdit ? (
              <>
                <Label>{t('warehouse.form.name')}</Label>
                <Input
                  value={field.value}
                  disabled
                  variant="secondary"
                />
              </>
            ) : (
              <ArticleAutocomplete
                label={t('warehouse.form.name')}
                placeholder="Выберите деталь"
                selectedKey={field.value}
                onSelectionChange={val => field.onChange(val || '')}
                isInvalid={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                isRequired
                allowCustomValue={false}
              />
            )
          )}
        />
      </div>

      {/* Vehicle select */}
      <div className="flex flex-col gap-1">
        <VehicleAutocomplete
          label={t('warehouse.form.vehicle')}
          placeholder={t('warehouse.form.vehicle_placeholder')}
          isRequired
          selectedId={vehicleId}
          initialValue={initialVehicle}
          onSelectionChange={vehicle => {
            if (vehicle) {
              setVehicleId(vehicle.ID)
              setVehiclePlate(vehicle.Number)
            } else {
              setVehicleId('none')
              setVehiclePlate(null)
            }
          }}
        />
      </div>

      {/* Date */}
      <div className="flex flex-col gap-1">
        <Controller
          name="date"
          control={control}
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label={t('warehouse.form.install_date')}
              required
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>

      {/* Count */}
      <div className="flex flex-col gap-1">
        <Label htmlFor="install-count" isRequired>{t('warehouse.form.count')}</Label>
        <Input
          id="install-count"
          type="number"
          min="1"
          step="1"
          className={errors.count ? 'border-danger' : ''}
          {...register('count', { valueAsNumber: true })}
          onKeyDown={e => {
            if (
              [
                'Backspace',
                'Tab',
                'Enter',
                'Escape',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
              ].includes(e.key) ||
              ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
            ) {
              return
            }
            if (!/^[0-9]$/.test(e.key)) {
              e.preventDefault()
            }
          }}
          variant={'secondary'}
        />
        {errors.count?.message && <span className="text-xs text-danger">{errors.count.message}</span>}
      </div>

      <div className="flex gap-3 pt-4 justify-end">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" isDisabled={isLoading}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEdit ? t('common.save') : t('warehouse.form.submit_install')}
            </div>
          ) : (
            isEdit ? t('common.save') : t('warehouse.form.submit_install')
          )}
        </Button>
      </div>
    </form>
  )
}
